/**
 * Alert component showing pending/challenged hold status and urgency
 */

import React from 'react';
import { Alert, AlertTitle } from '@mui/material';
import type { HoldList } from 'api-client/models';

interface HoldDecisionAlertProps {
  holds: HoldList[];
}

export const HoldDecisionAlert: React.FC<HoldDecisionAlertProps> = ({ holds }) => {
  const pendingHolds = holds.filter((h) => ['pending', 'challenged'].includes(h.status));
  const confirmedHolds = holds.filter((h) => h.status === 'confirmed');
  const releasedHolds = holds.filter((h) => h.status === 'released');

  // Only show alert if there are pending holds AND some confirms/releases have happened
  if (pendingHolds.length === 0 || (confirmedHolds.length === 0 && releasedHolds.length === 0)) {
    return null;
  }

  // Check for holds with active decision deadlines (24-hour deadline)
  const now = new Date();
  const pendingWithDeadline = pendingHolds.filter((h) => {
    if (!h.decisionDeadline) return false;
    const deadline = new Date(h.decisionDeadline);
    return deadline > now;
  });

  const hasDeadline = pendingWithDeadline.length > 0;
  const shortestDeadline = hasDeadline
    ? pendingWithDeadline.reduce((shortest, hold) => {
        const holdDeadline = new Date(hold.decisionDeadline!);
        const shortestDeadline = new Date(shortest.decisionDeadline!);
        return holdDeadline < shortestDeadline ? hold : shortest;
      })
    : null;

  const formatTimeRemaining = (decisionDeadline: Date) => {
    const hoursRemaining = Math.floor((decisionDeadline.getTime() - now.getTime()) / (1000 * 60 * 60));
    const minutesRemaining = Math.floor(
      ((decisionDeadline.getTime() - now.getTime()) % (1000 * 60 * 60)) / (1000 * 60)
    );
    return `${hoursRemaining}h ${minutesRemaining}m`;
  };

  // Determine severity based on time remaining
  const isUrgent =
    hasDeadline && shortestDeadline
      ? (new Date(shortestDeadline.decisionDeadline!).getTime() - now.getTime()) / (1000 * 60 * 60) < 6
      : false;

  return (
    <Alert severity={isUrgent ? 'warning' : 'info'} sx={{ mb: 2 }}>
      <AlertTitle>
        {isUrgent ? 'Urgent: Decision Required' : 'Decision Required for Pending Holds'}
      </AlertTitle>
      You have {pendingHolds.length} hold{pendingHolds.length !== 1 ? 's' : ''} pending decision
      {confirmedHolds.length > 0 && ` (${confirmedHolds.length} confirmed)`}
      {releasedHolds.length > 0 && ` (${releasedHolds.length} released)`}.
      {hasDeadline && shortestDeadline && (
        <>
          {' '}
          <strong>
            Shortest deadline: {formatTimeRemaining(new Date(shortestDeadline.decisionDeadline!))} remaining
          </strong>
        </>
      )}
      {hasDeadline ? (
        <>
          {' '}
          If you don't respond before the deadline, the challenged hold will be automatically released and the
          challenger will take your position.
        </>
      ) : (
        <>
          {' '}
          These holds have no active challenge deadline, but you should decide soon to confirm your show dates.
        </>
      )}
    </Alert>
  );
};
